import {
  fromEvent,
  map,
  merge,
  Subscription,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import {
  WizDraggableItem,
  WizDropItemEvent,
} from '../../directives/behavior/drag-drop/wiz-drag-drop.model';
import { WizDragDropContainer } from './../../directives/behavior/drag-drop/wiz-drag-drop.directive';

/** manager pour gestion du dragNdrop */
export class WizDndManager {
  /** Cibles de drop potentielles */
  private static dropTargets: Map<any, WizDragDropContainer> = new Map();

  /** Enregistrement d'une cible potentielle de drop */
  public static registerDropTarget(pComponent: WizDragDropContainer): void {
    this.dropTargets.set(pComponent.elementRef.nativeElement, pComponent);
  }

  public static unRegisterDropTarget(pComponent: WizDragDropContainer): void {
    this.dropTargets.delete(pComponent.elementRef.nativeElement);
  }

  // Stream du drag/drop en cours.
  private static _currentDrag$: Subscription;

  /** Indique si une action de drag/drop est en cours */
  public static isActive(): boolean {
    return this._currentDrag$ && !this._currentDrag$.closed;
  }

  /**
   * MouseDown sur un contrôle draggable.
   * Déclenchement des écouteurs sur hold si release ou move AVANT le premier hit, pas d'initialisation du drag'n'drop
   */
  public static startDragAction(
    pItem: WizDraggableItem,
    pSourceContainer: WizDragDropContainer
  ): void {
    const draggedElt: any = pItem.target; // élément HTML
    if (this._currentDrag$) {
      this._currentDrag$.unsubscribe();
    }
    this._currentDrag$ = fromEvent(draggedElt, 'mousemove')
      .pipe(
        takeUntil(fromEvent(draggedElt, 'mouseup')),
        tap(() => (draggedElt.draggable = true)),
        map(() => Array.from(WizDndManager.dropTargets.values())),
        switchMap((dropTargets: WizDragDropContainer[]) =>
          merge(
            ...dropTargets.map((dropTarget) =>
              fromEvent(dropTarget.elementRef.nativeElement, 'dragover')
            ),
            ...dropTargets.map((dropTarget) =>
              fromEvent(dropTarget.elementRef.nativeElement, 'drop')
            ),
            ...dropTargets.map((dropTarget) =>
              fromEvent(dropTarget.elementRef.nativeElement, 'dragleave')
            ),
            ...dropTargets.map((dropTarget) =>
              fromEvent(dropTarget.elementRef.nativeElement, 'dragenter')
            )
          ).pipe(
            takeUntil(
              fromEvent(draggedElt, 'dragend').pipe(
                tap(() => {
                  WizDndManager.dropTargets.forEach((target) =>
                    target.onDragEnd()
                  );
                  this._currentDrag$.unsubscribe();
                })
              )
            )
          )
        )
      )
      .subscribe((event: DragEvent) => {
        const dropTarget: WizDragDropContainer = WizDndManager.dropTargets.get(
          event.currentTarget
        );
        const isSelfDrop: boolean = pSourceContainer === dropTarget;
        if (dropTarget) {
          const dragEvent: WizDropItemEvent = {
            item: pItem,
            event,
            targetPosition: dropTarget.getDropPosition(event),
            dragSource: pSourceContainer.draggableContainer,
          };
          switch (event.type) {
            case 'dragover':
              // On teste si le drop est possible sur le container cible :
              if (dropTarget.onDragOver(dragEvent, isSelfDrop)) {
                event.preventDefault();
              }
              break;
            case 'dragleave':
              dropTarget.onDragLeave(dragEvent); 
              event.preventDefault();
              break;
            case 'dragenter':
              dropTarget.onDragEnter(dragEvent); 
              event.preventDefault();
              break;
            case 'drop':
              event.preventDefault();
              // Drag/drop interne géré différement du drop sur source externe
              if (isSelfDrop) {
                dropTarget.onSelfDrop(dragEvent);
              } else {
                // Drop function sur le container de destination
                dropTarget.onDrop(dragEvent, false);
                pSourceContainer.onDrop(dragEvent, true);
              }
              break;
            default:
              break;
          }
        }
      });
  }

  /**
   * Données de drag & drop enregistrées. Utilisé pour transmettre de la donnée complexe pour les drag & drop
   */
  private static dragData: Map<string, any> = new Map();

  /**
   * Enregistre des données de drag & drop
   * @param id identifiant de la donnée
   * @param data les données associées
   */
  public static setDragData(id: string, data: any): void {
    this.dragData.set(id, data);
  }

  /**
   * Accède aux données de drag & drop par l'identifiant
   * @param id L'identifiant de la donnée
   */
  public static getDragData(id: string): any {
    if (this.dragData.has(id)) {
      const result: any = this.dragData.get(id);
      this.dragData.delete(id);
      return result;
    }
    return null;
  }
}
